import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Switch,
  message,
  Alert,
  Divider,
  Descriptions,
  Tag,
  Spin,
  Modal,
  Typography,
} from 'antd';
import {
  MailOutlined,
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import api, { get } from '../../utils/api';

const { Text, Paragraph } = Typography;

const EmailConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testForm] = Form.useForm();
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchEmailConfig();
    fetchEmailStatus();
  }, []);

  // 获取邮件配置
  const fetchEmailConfig = async () => {
    setLoading(true);
    try {
      const res = await get('/admin/email/config');
      if (res.code === 200) {
        const configs = res.data?.configs || [];
        const configMap = {};
        configs.forEach(config => {
          let value = config.config_value;
          // 跳过已隐藏的敏感信息
          if (value === '***已设置***') {
            return;
          }
          if (config.config_type === 'boolean') {
            value = value === 'true' || value === '1';
          }
          configMap[config.config_key] = value;
        });
        form.setFieldsValue(configMap);
      }
    } catch (error) {
      message.error('获取配置失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 获取邮件服务状态
  const fetchEmailStatus = async () => {
    try {
      const res = await get('/admin/email/status');
      if (res.code === 200) {
        setStatus(res.data);
      }
    } catch (error) {
      console.error('获取邮件服务状态失败:', error);
    }
  };

  // 保存配置
  const handleSave = async (values) => {
    setSubmitting(true);
    try {
      // 转换配置格式
      const configs = {};
      Object.keys(values).forEach(key => {
        configs[key] = typeof values[key] === 'boolean' ? String(values[key]) : values[key];
      });

      const res = await api.put('/admin/email/config', { configs });
      
      if (res.code === 200) {
        message.success('配置保存成功');
        fetchEmailStatus(); // 刷新状态
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (error) {
      message.error(error.message || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 打开测试弹窗
  const handleOpenTest = () => {
    testForm.resetFields();
    setTestModalVisible(true);
  };

  // 发送测试邮件
  const handleSendTest = async (values) => {
    setTesting(true);
    try {
      const res = await api.post('/admin/email/test/verification-code', {
        to: values.testEmail,
      });
      
      if (res.code === 200) {
        message.success(`测试邮件发送成功！验证码：${res.data.testCode}`);
        setTestModalVisible(false);
      } else {
        message.error(res.message || '发送失败');
      }
    } catch (error) {
      message.error(error.message || '发送失败');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      <Card
        title={
          <Space>
            <MailOutlined />
            <span>邮件服务配置</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<SendOutlined />}
              onClick={handleOpenTest}
              disabled={!status?.configured}
            >
              发送测试邮件
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchEmailConfig();
                fetchEmailStatus();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {/* 服务状态 */}
        {status && (
          <Alert
            message="服务状态"
            description={
              <Descriptions column={2} size="small">
                <Descriptions.Item label="服务状态">
                  {status.enabled ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>已启用</Tag>
                  ) : (
                    <Tag color="default" icon={<CloseCircleOutlined />}>已禁用</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="配置状态">
                  {status.configured ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>已配置</Tag>
                  ) : (
                    <Tag color="error" icon={<CloseCircleOutlined />}>未配置</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="API地址">
                  {status.hasApiUrl ? (
                    <Tag color="success">已设置</Tag>
                  ) : (
                    <Tag color="error">未设置</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="发件人邮箱">
                  {status.hasMailFrom ? (
                    <Tag color="success">已设置</Tag>
                  ) : (
                    <Tag color="error">未设置</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="邮箱密码">
                  {status.hasPassword ? (
                    <Tag color="success">已设置</Tag>
                  ) : (
                    <Tag color="error">未设置</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="初始化">
                  {status.initialized ? (
                    <Tag color="success">已初始化</Tag>
                  ) : (
                    <Tag color="default">未初始化</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
            }
            type={status.configured ? "success" : "warning"}
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {/* 配置说明 */}
        <Alert
          message="配置说明"
          description={
            <div>
              <Paragraph>
                本系统使用宝塔邮箱服务作为邮件发送服务。
              </Paragraph>
              <Paragraph>
                <Text strong>配置步骤：</Text>
                <ol style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>确保已安装宝塔面板邮局管理器插件</li>
                  <li>在宝塔面板中创建邮箱账号</li>
                  <li>获取邮件服务API地址（例如：http://your_panel_address/mail_sys/send_mail_http.json）</li>
                  <li>在下方填写配置信息并保存</li>
                </ol>
              </Paragraph>
            </div>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24 }}
        />

        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
          >
            <Divider orientation="left">基础配置</Divider>

            <Form.Item
              label="启用邮件服务"
              name="email_enable"
              valuePropName="checked"
              extra="关闭后将不会发送任何邮件"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>

            <Form.Item
              label="邮件服务API地址"
              name="email_api_url"
              rules={[{ required: true, message: '请输入邮件服务API地址' }]}
              extra="例如：http://your_panel_address/mail_sys/send_mail_http.json"
            >
              <Input placeholder="http://your_panel_address/mail_sys/send_mail_http.json" />
            </Form.Item>

            <Form.Item
              label="发件人邮箱地址"
              name="email_from"
              rules={[
                { required: true, message: '请输入发件人邮箱地址' },
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
              extra="例如：noreply@yourdomain.com"
            >
              <Input placeholder="noreply@yourdomain.com" />
            </Form.Item>

            <Form.Item
              label="发件人邮箱密码"
              name="email_password"
              rules={[{ required: true, message: '请输入发件人邮箱密码' }]}
              extra="发件人邮箱的登录密码"
            >
              <Input.Password placeholder="请输入邮箱密码" />
            </Form.Item>

            <Divider orientation="left">邮件模板配置</Divider>

            <Form.Item
              label="验证码邮件主题"
              name="email_verification_subject"
            >
              <Input placeholder="Your Verification Code" />
            </Form.Item>

            <Form.Item
              label="欢迎邮件主题"
              name="email_welcome_subject"
            >
              <Input placeholder="Welcome to Our Platform" />
            </Form.Item>

            <Form.Item
              label="密码重置邮件主题"
              name="email_password_reset_subject"
            >
              <Input placeholder="Password Reset Request" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={submitting}
                >
                  保存配置
                </Button>
                <Button onClick={() => form.resetFields()}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>

      {/* 测试邮件弹窗 */}
      <Modal
        title={
          <Space>
            <SendOutlined />
            <span>发送测试邮件</span>
          </Space>
        }
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        onOk={() => testForm.submit()}
        confirmLoading={testing}
      >
        <Alert
          message="将发送一封包含验证码的测试邮件"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form
          form={testForm}
          layout="vertical"
          onFinish={handleSendTest}
        >
          <Form.Item
            label="收件人邮箱"
            name="testEmail"
            rules={[
              { required: true, message: '请输入收件人邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="test@example.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailConfig;
